import React, { useState, useEffect, useRef } from 'react';
import './PDPHero.scss';
import { BREAKPOINTS } from '../../../constants/breakpoints';
import Modal from 'react-modal';
import CTAButton from '../../UI/CTA/CTAButton/CTAButton';
import { videoDataLayer } from '../../../util/videoDataLayer';

const bodyScrollLock = require('body-scroll-lock');
const disableBodyScroll = bodyScrollLock.disableBodyScroll;
const enableBodyScroll = bodyScrollLock.enableBodyScroll;

const DESKTOP_WIDTH = BREAKPOINTS.large;

/**
 * Render hero for PDP template
 * @param {Object} product
 * @param {String} anchor
 * @param {String} heading
 * @param {Object} video
 * @param {String} compare
 * @param {String} shipping
 * @param {String} returns
 * @param {String} warranty
 * @returns {JSX.Element}
 * @constructor
 */
const PDPHero = ({ product, anchor, heading, video, compare, shipping, returns, warranty }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < DESKTOP_WIDTH);
  const modal = useRef();
  let content;

  useEffect(() => {
    setIsMobile(window.innerWidth < DESKTOP_WIDTH);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleResize = () => setIsMobile(window.innerWidth < DESKTOP_WIDTH);

  const contentRefCallback = (node) => {
    const { dataLayer } = window;
    if (modalOpen) {
      if (node) {
        content = node;
        const video = content.querySelector('video');

        if (video) {
          videoDataLayer(video, dataLayer);
        }
      }
    }
  };

  const toggleModal = () => {
    setModalOpen(!modalOpen);
    modalOpen ? enableBodyScroll(modal) : disableBodyScroll(modal);
  };

  const afterModalOpen = () => disableBodyScroll(modal);
  const afterModalClose = () => enableBodyScroll(modal);

  const renderDetails = () => {
    return (
      <div className="c-pdp-hero__details">
        <div className="c-pdp-hero__cta-container">
          {video.text && (
            <CTAButton
              isButton={true}
              type={'pdp-hero'}
              onClick={toggleModal}
              text={' Watch Video'}
              additionalClasses={'cta color-blue'}
              isPlay={true}
            />
          )}
          {compare && (
            <a className="c-pdp-hero__compare-cta color-black cta text-cta" href="#compare">
              {compare}
            </a>
          )}
        </div>
        <p className="c-pdp-hero__desc body-1 color-black">{product.desc}</p>
        <div className="c-pdp-hero__highlights">
          {isMobile ? (
            <>
              <div className="c-pdp-hero__highlights-top tag color-black">
                <span>{shipping}</span>
                &nbsp;&nbsp;&nbsp;&nbsp;•&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                <span>{returns}</span>
              </div>
              <div className="c-pdp-hero__highlights-bottom tag color-black">{warranty}</div>
            </>
          ) : (
            <>
              <div className="tag color-black">
                {shipping}
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;• &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                {returns}
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;• &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                {warranty}
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="c-pdp-hero">
      {anchor && <a href={anchor} />}
      <div className="c-pdp-hero__content">
        {isMobile ? (
          <>
            <h3 className="c-pdp-hero__heading h2-right-grotesk color-black">{heading}</h3>
            <img className="c-pdp-hero__img" src={product.img} alt="Product Image" />
            {renderDetails()}
          </>
        ) : (
          <>
            <div className="c-pdp-hero__leading">
              <h3 className="c-pdp-hero__heading h2-right-grotesk color-black">{heading}</h3>
              {renderDetails()}
            </div>
            <div className="c-pdp-hero__trailing">
              <div className="c-pdp-hero__img-wrapper">
                <img className="c-pdp-hero__img" src={product.img} alt={product?.title} />
              </div>
            </div>
          </>
        )}
      </div>

      {modalOpen && (
        <Modal
          ref={modal}
          isOpen={modalOpen}
          onAfterOpen={afterModalOpen}
          onRequestClose={afterModalClose}
          className="c-home-hero__modal"
          styles={{ content: { zIndex: 2 } }}
          overlayClassName={'c-home-hero__modal-overlay'}
          contentRef={contentRefCallback}
        >
          <div className="c-pdp-hero__modal-overlay" onClick={toggleModal}></div>
          <div className="c-pdp-hero__modal-inner">
            <video
              className="c-pdp-hero__modal-video"
              src={video.url}
              autoPlay={true}
              muted={false}
              controls={true}
            >
              <source src={video.url} />
            </video>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default PDPHero;
